import { useMemo } from 'react';

import { cn, Stack, Typography } from '@crossnokaye/ux-common';
import { Input } from '@crossnokaye/ux-common/ckds';

import {
  ButtonComponentActionConfigVariant,
  TableColumnConfig,
  TableRowConfig,
} from '@repo/sdk/models/components';

import { ActionDropdownDisplay } from '@/features/equipment-browser/action-components/action-dropdown-display';

import {
  getColumnInputType,
  getColumnUnitInfo,
  getCommonDisplayValue,
  getOptionsForColumn,
} from '../equipment-bulk-edit.helpers';

interface EquipmentBulkEditSettingProps {
  column: TableColumnConfig;
  onSettingFocus?: (columnId: string) => void;
  onSettingBlur?: () => void;
  onValueChange: (columnId: string, value: string) => void;
  rows: TableRowConfig[];
  selectedRowIds: string[];
  value: string | undefined;
}

/**
 * Renders a single column's bulk-edit control (dropdown or text input)
 * with dirty-state visual feedback (yellow text) when a value has been set.
 * Converts from source units to display units for display, and converts
 * back from display units to source units on change.
 */
export const EquipmentBulkEditSetting = ({
  column,
  onSettingFocus,
  onSettingBlur,
  onValueChange,
  rows,
  selectedRowIds,
  value,
}: EquipmentBulkEditSettingProps) => {
  const inputType = getColumnInputType(column.columnId, rows, selectedRowIds);
  const unitsLabel = getColumnUnitInfo(column.columnId, rows, selectedRowIds)?.unitsLabel;

  const commonDisplayValue = useMemo(
    () => getCommonDisplayValue(column.columnId, inputType, rows, selectedRowIds),
    [column.columnId, inputType, rows, selectedRowIds]
  );

  // Don't render readonly columns
  if (inputType === 'readonly') return null;

  const isDirty = !!value && value !== commonDisplayValue;

  return (
    <Stack key={column.columnId} gap="xs">
      <Typography variant="body">{column.label ?? column.columnId}</Typography>
      {inputType === 'dropdown' ? (
        <div
          onFocus={() => onSettingFocus?.(column.columnId)}
          onBlur={(event) => {
            const nextFocusedElement = event.relatedTarget;
            if (
              nextFocusedElement instanceof Node &&
              event.currentTarget.contains(nextFocusedElement)
            ) {
              return;
            }
            onSettingBlur?.();
          }}
        >
          <ActionDropdownDisplay
            buttonVariant={ButtonComponentActionConfigVariant.Tier3}
            className={cn(isDirty && 'text-ckds-base-caution', 'w-full')}
            onSelect={(option) => onValueChange(column.columnId, option.value)}
            options={getOptionsForColumn(column.columnId, rows, selectedRowIds)}
            placeholder="Select value to change to"
            value={value}
          />
        </div>
      ) : (
        <Input
          aria-label={column.label ?? column.columnId}
          className={cn(isDirty && 'text-ckds-base-caution')}
          inlineLabel={unitsLabel}
          onChange={(e) => onValueChange(column.columnId, e.target.value)}
          onBlur={() => onSettingBlur?.()}
          onFocus={() => onSettingFocus?.(column.columnId)}
          placeholder="Enter value to change to"
          value={value}
        />
      )}
    </Stack>
  );
};
